/** Core domain types for ZoneIn AI. */

export type ZoneId = 'zone1' | 'zone2' | 'zone3' | 'zone4';

export interface ZoneMeta {
  id: ZoneId;
  title: string;
  subtitle: string;
  emoji: string;
  /** Tailwind utility fragments used by the accordion UI (dark theme). */
  color: {
    border: string;
    headerBg: string;
    text: string;
    badgeBg: string;
    badgeText: string;
    bar: string;
  };
}

export interface ActionStep {
  id: string;
  text: string;
  /** Estimated minutes to complete. */
  minutes: number;
  done: boolean;
  /** Epoch ms when marked in-progress (tap cycle: blank → started → done). */
  startedAt?: number;
}

export interface Task {
  id: string;
  text: string;
  zone: ZoneId;
  /** 0–100 signal scores from the classifier (kept for transparency/debug). */
  urgency: number;
  importance: number;
  /** Detected natural-language deadline, if any (e.g. "by Friday"). */
  deadlineHint?: string;
  /**
   * Machine-readable target timestamp (epoch ms) — present only when the
   * date was set via chips/pickers. Free-text hints store no timestamp:
   * an honest absence beats a guessed one. Powers date sort (Phase D).
   */
  deadlineAt?: number;
  steps: ActionStep[];
  /** Steps are generated lazily on first drill-down. */
  stepsGenerated: boolean;
  done: boolean;
  /** Tag ids (projects / roles / deliverables) — see engine/tags.ts.
   *  A task can belong to a project AND a role; order = assignment order. */
  tags?: string[];
  /** Epoch ms when marked in-progress — multi-day work reads as underway,
   *  not untouched. Cleared when the task resets to blank. */
  startedAt?: number;
  /** Free-form working note ("where I left off") — the parking space that
   *  makes switching between tasks safe. */
  note?: string;
  /** Hand-order rank within a zone's group of tasks due the SAME DAY (or
   *  the rough-date / undated group) — engine/manualOrder.ts. Sparse until
   *  the user's first reorder; cleared when the task's date moves it to
   *  another group. Dates always decide across days. (V2 §4e; same-day
   *  groups added 15 Oct 2026.) */
  manualRank?: number;
  /** "Not today" deferral (epoch ms): while this resolves to a FUTURE local
   *  day, the Today lens excludes the task (V2_DESIGN_SPEC §1/§3). Set by the
   *  V2-B swipe; declared now so the engine and sync carry it safely. */
  deferredUntil?: number;
  /** How many weekly rollovers this task has survived without completion. */
  rolloverCount: number;
  /** Weekly Insights hand-off (13 Oct 2026, web-first): the person this
   *  task was handed to (≤ HANDED_TO_MAX chars). Its check-back date is
   *  `deferredUntil` — the task leaves Today until then. Rides inside the
   *  task through sync (whole-task merge), so older builds carry it. */
  handedTo?: string;
  /** Recurring task (18 Oct 2026, engine/recurrence.ts): ticking it done
   *  creates the next copy, dated by this rule. Absent = does not repeat.
   *  Rides inside the task through sync, so older builds carry it. */
  repeat?: 'day' | 'weekday' | 'week' | 'month';
  createdAt: number;
  /** Last user edit on this task — drives per-task sync conflict merging. */
  editedAt?: number;
}

/** A completed task snapshot kept for weekly analytics. */
export interface ArchivedTask {
  /** Source task id — archive entries are matched by id, never by text. */
  taskId: string;
  text: string;
  zone: ZoneId;
  completedAt: number;
}

/** One closed weekly cycle in the Mastery log. */
export interface WeekRecord {
  id: string;
  label: string;
  endedAt: number;
  completedCount: number;
  rolledOverCount: number;
  /** Percentage of completed work per zone (0–100 each). */
  distribution: Record<ZoneId, number>;
}

export type ImportStrategy = 'merge' | 'replace';

/** Optional image attachment for multimodal (paper list / sticky note) scans. */
export interface ScanAttachment {
  mimeType: string;
  /** Raw base64 payload (no data: prefix). */
  dataBase64: string;
  /** Data URL for thumbnail preview. */
  previewUrl: string;
  fileName: string;
  /** Batch sticky-notes mode: parse each note as a discrete task. */
  batchStickyMode: boolean;
}

/** Result shape every AI provider must return for a classification pass. */
export interface ClassifiedTask {
  text: string;
  zone: ZoneId;
  urgency: number;
  importance: number;
  deadlineHint?: string;
  /** Scan detected a tick/strikethrough — completed work, not a to-do.
   *  Routed to the user's review, never silently imported or dropped. */
  alreadyDone?: boolean;
}

export interface AiProvider {
  /** Human-readable provider name (shown in Settings). */
  readonly name: string;
  /** True when the provider can read images (paper scans, sticky notes). */
  readonly supportsVision: boolean;
  /** Split + classify a raw brain dump (and optional scans) into tasks. */
  classify(rawInput: string, scans?: ScanAttachment[]): Promise<ClassifiedTask[]>;
  /** Expand a single task into an execution roadmap. */
  expand(taskText: string, zone: ZoneId): Promise<Omit<ActionStep, 'id' | 'done'>[]>;
  /**
   * Living plans: merge a user's update note ("what changed") into an
   * existing plan. Providers receive completed steps as CONTEXT ONLY and
   * return the revised list of OPEN steps — the caller preserves completed
   * steps verbatim (see engine/planUpdate.ts for the enforced invariant).
   */
  revise(
    taskText: string,
    zone: ZoneId,
    completed: ActionStep[],
    open: ActionStep[],
    note: string,
  ): Promise<Omit<ActionStep, 'id' | 'done'>[]>;
}

/**
 * 'quarterly' is legacy — no longer sold (removed from PLANS), but the type
 * remains so existing quarterly subscriptions stay valid and premium.
 */
export type PaidPlan = 'monthly' | 'quarterly' | 'annual' | 'lifetime';
export type PlanId = 'free' | 'trial' | PaidPlan;

export interface PlanMeta {
  id: PaidPlan;
  title: string;
  subtitle: string;
  price: string;
  /** Whether this plan includes the 7-day free trial. */
  trial: boolean;
}

export const PLANS: PlanMeta[] = [
  {
    id: 'annual',
    title: 'Annual Membership (7-Day Free Trial)',
    subtitle: 'Just $2.50/mo — save 50% vs monthly.',
    price: '$29.99/yr',
    trial: true,
  },
  {
    id: 'lifetime',
    title: 'Lifetime Pass (Pay Once)',
    subtitle: 'Launch window only — on sale until 31 March 2027 or 500 passes, then it retires.',
    price: '$69.99',
    trial: false,
  },
  {
    id: 'monthly',
    title: 'Monthly Access',
    subtitle: 'Flexible monthly rolling billing.',
    price: '$4.99/mo',
    trial: false,
  },
];

export interface Subscription {
  plan: PlanId;
  /** Epoch ms when trial started; undefined if never started. */
  trialStartedAt?: number;
  /** Selected paid plan the trial converts into. */
  pendingPlan?: PaidPlan;
  /** Granted via alpha invite code — honored as lifetime when real billing ships. */
  alphaTester?: boolean;
}

export interface BillingProvider {
  readonly name: string;
  /** Start the 7-day trial (annual) or activate a plan directly (mock). */
  subscribe(plan: PaidPlan): Promise<Subscription>;
  cancel(): Promise<Subscription>;
  restore(): Promise<Subscription | null>;
  /** Consumable in-app purchase: camera scan token pack. */
  purchaseScanPack(): Promise<number>;
}

/**
 * Shown in Settings. Bump WITH `package.json` "version" and
 * `android/app/build.gradle` versionName — three sites, one release.
 */
export const APP_VERSION = '2.2.1';

export const TRIAL_DURATION_MS = 7 * 24 * 60 * 60 * 1000;
export const FREE_SCANS = 3;
/**
 * Free-tier AI Action Plan samples. Two, deliberately: the first is often
 * spent on a trivial task; the second is where users pick a real task and
 * the value lands. The paywall appears on the THIRD attempt — at peak
 * desire, never blocking the aha moment.
 */
export const FREE_PLANS = 2;
export const SCAN_PACK_SIZE = 15;
export const SCAN_PACK_PRICE = '$0.99';
/** A task rolled over this many weeks triggers the fatigue audit. */
export const FATIGUE_THRESHOLD = 3;

export type Remediation = 'split' | 'delegate' | 'drop' | 'keep';

export const ZONES: ZoneMeta[] = [
  {
    id: 'zone1',
    title: 'Critical & Immediate',
    subtitle: 'Crises and hard deadlines — do these now',
    emoji: '🔴',
    color: {
      border: 'border-red-200 dark:border-red-500/10',
      headerBg: 'bg-red-50 dark:bg-red-950/20',
      text: 'text-red-600 dark:text-red-400',
      badgeBg: 'bg-red-500/10 border border-red-200 dark:border-red-500/20',
      badgeText: 'text-red-600 dark:text-red-400',
      bar: 'bg-red-500',
    },
  },
  {
    id: 'zone2',
    title: 'Strategic & Growth',
    subtitle: 'High-value work — schedule and protect it',
    emoji: '🟢',
    color: {
      border: 'border-emerald-200 dark:border-emerald-500/10',
      headerBg: 'bg-emerald-50 dark:bg-emerald-950/20',
      text: 'text-emerald-600 dark:text-emerald-400',
      badgeBg: 'bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20',
      badgeText: 'text-emerald-600 dark:text-emerald-400',
      bar: 'bg-emerald-500',
    },
  },
  {
    id: 'zone3',
    title: 'Distractions & Demands',
    subtitle: 'Urgent for others — delegate, batch, or timebox',
    emoji: '🟡',
    color: {
      border: 'border-amber-200 dark:border-amber-500/10',
      headerBg: 'bg-amber-50 dark:bg-amber-950/20',
      text: 'text-amber-600 dark:text-amber-400',
      badgeBg: 'bg-amber-500/10 border border-amber-200 dark:border-amber-500/20',
      badgeText: 'text-amber-600 dark:text-amber-400',
      bar: 'bg-amber-500',
    },
  },
  {
    id: 'zone4',
    title: 'Time Drains & Diversions',
    subtitle: 'Friction loops — cut these to reclaim time',
    emoji: '⚫',
    color: {
      border: 'border-slate-300 dark:border-slate-500/10',
      headerBg: 'bg-slate-100 dark:bg-slate-900',
      text: 'text-slate-500 dark:text-slate-400',
      badgeBg: 'bg-slate-500/10 border border-slate-300 dark:border-slate-500/20',
      badgeText: 'text-slate-500 dark:text-slate-400',
      bar: 'bg-slate-600',
    },
  },
];

export const ZONE_BY_ID: Record<ZoneId, ZoneMeta> = Object.fromEntries(
  ZONES.map((z) => [z.id, z]),
) as Record<ZoneId, ZoneMeta>;

/**
 * Canonical cross-device task ordering: open tasks before done, then
 * creation order, with the id as a deterministic tiebreaker. Every view
 * (matrix, focus, print, exports) sorts with this so the order is IDENTICAL
 * on every device — storage layers (Firestore maps) don't preserve insertion
 * order, so order must be derived from the data, never from storage.
 */
export function compareTasks(a: Task, b: Task): number {
  if (a.done !== b.done) return Number(a.done) - Number(b.done);
  if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

/** ISO-8601 week label, e.g. "Week 29, 2026". */
export function weekLabel(date: Date = new Date()): string {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
  return `Week ${week}, ${d.getUTCFullYear()}`;
}
